/**
 * Anlagendokumentation nach OVE/OENORM E-8101 — und zugleich das Datenblatt
 * fuer die Meldung beim Netzbetreiber.
 *
 * Verlangt wird ein Pruefprotokoll nach dieser Norm; die Vorlage der
 * Bundesinnung (ueber das KFE) und die der Kommunalkredit Public Consulting
 * haben denselben Aufbau: Pruefbefund, Anlagendokumentation, Pruefung.
 *
 * Was hier entsteht, ist der Teil, den das Programm ausfuellen kann —
 * Abschnitt 3, die technischen Daten der Anlage. Modultype, Anzahl Straenge,
 * Module je Strang, Systemspannung, Ausrichtung, Neigung, Wechselrichter,
 * Speicher, Leitungen: alles davon steht bereits im Projekt. Der Elektriker
 * traegt nur noch ein, was gemessen wurde.
 *
 * Zwei Dinge, die das Blatt NICHT ist:
 *   - Es ersetzt das amtliche Formular nicht. Es fuellt dessen Inhalt vor.
 *   - Es verschickt nichts. Die Fertigstellungsanzeige bringt das
 *     Elektrounternehmen selbst im Netzpartner-Portal ein — eine Schnittstelle
 *     dorthin gibt es nicht.
 */

import { Fragment } from 'react';

import {
  himmelsrichtung,
  planungspunkte,
  pruefstand,
  punkteAusVorlage,
  stringSpannung,
  type Pruefvorlage,
} from '@pv/geometrie';

import type { Anlagendaten } from '../anlage.ts';
import { tagAnzeige, tagVon } from '../datum.ts';
import type { Planung } from '../planung.ts';
import type {
  Errichterangaben,
  Flaeche,
  Komponente,
  Modul,
  Projekt,
  Projektkomponente,
  Pruefbefundzeile,
  Stringzeile,
} from '../typen.ts';

interface Eigenschaften {
  projekt: Projekt;
  flaechen: Flaeche[];
  planungen: Map<string, Planung>;
  strings: Stringzeile[];
  projektkomponenten: Projektkomponente[];
  komponenten: Komponente[];
  module: Modul[];
  /** Welches Modul auf welcher Fläche liegt — aus den Belegungen. */
  modulJeFlaeche: Map<string, string>;
  pruefbefund: Pruefbefundzeile | null;
  /** Für die Punkte aus der Planung (Stringspannung, Spannungsfall, …). */
  anlage: Anlagendaten;
  /** Solange der Befund keine eigenen Punkte hat, stehen die der Vorlage offen da. */
  pruefvorlage: Pruefvorlage;
  errichter: Errichterangaben;
  netzvorgaben: { netzbetreiber?: string; portal?: string; hinweis?: string };
  leistungKwp: number;
  onSchliessen: () => void;
}

export function Anlagendokumentation(e: Eigenschaften): React.JSX.Element {
  const offeneStrings = e.strings.filter((s) => !s.geloescht_am);
  const offeneKomponenten = e.projektkomponenten.filter((k) => !k.geloescht_am);
  const stamm = (pk: Projektkomponente): Komponente | undefined =>
    e.komponenten.find((k) => k.id === pk.komponente_id);

  const wechselrichter = offeneKomponenten.filter((pk) => stamm(pk)?.art === 'wechselrichter');
  const speicher = offeneKomponenten.filter((pk) => stamm(pk)?.art === 'speicher');
  const ueberspannung = offeneKomponenten.filter((pk) => stamm(pk)?.art === 'ueberspannungsschutz');

  // Das verwendete Modul — die Anlagendokumentation kennt einen Typ je Anlage.
  // Vorher stand hier das erste aktive Modul des Pools, egal was verplant war:
  // „Groß 585“ auf dem Blatt, „Standard 440“ auf dem Dach, und die
  // Systemspannung mit den falschen Daten gerechnet (gefunden 11/2026).
  const modulId = e.flaechen
    .filter((f) => !f.geloescht_am && (e.planungen.get(f.id)?.zahlen.anzahl ?? 0) > 0)
    .map((f) => e.modulJeFlaeche.get(f.id))
    .find((id) => id != null);
  const modul = modulId ? e.module.find((m) => m.id === modulId) : undefined;

  // Straenge und Module je Strang, wie es im Datenblatt steht.
  const modulJeStrang = offeneStrings.map((s) => s.module.length);
  const gleicheLaenge = new Set(modulJeStrang).size <= 1;
  const systemspannung =
    modul && modulJeStrang.length > 0
      ? stringSpannung(
          Math.max(...modulJeStrang),
          { uocV: Number(modul.uoc_v), tkUocProzentProK: Number(modul.tk_uoc_prozent_k) },
          e.anlage.auslegung.kaltGradC,
          e.anlage.auslegung.referenzGradC,
        )
      : null;

  return (
    <div className="druckrahmen" onClick={e.onSchliessen}>
      <div className="druckblatt" onClick={(ereignis) => ereignis.stopPropagation()}>
        <div className="nurBildschirm knopfreihe" style={{ marginBottom: '1.5rem' }}>
          <button className="wichtig" onClick={() => window.print()}>
            Drucken oder als PDF sichern
          </button>
          <button onClick={e.onSchliessen}>Schließen</button>
        </div>

        <header style={{ borderBottom: '1px solid var(--rand)', paddingBottom: '1rem' }}>
          <p className="marke">Anlagendokumentation — Photovoltaikanlage</p>
          <h1>{e.projekt.kunde_name}</h1>
          <p style={{ margin: 0, fontSize: '0.85rem' }}>
            {[e.projekt.strasse, `${e.projekt.plz ?? ''} ${e.projekt.ort ?? ''}`.trim()]
              .filter(Boolean)
              .join(', ')}
          </p>
        </header>

        <Abschnitt nummer="1" titel="Allgemeine Angaben">
          <Zeile feld="Anlagenerrichtung (Jahr)" wert={tagVon(e.projekt.inbetriebnahme)?.slice(0, 4) ?? null} />
          <Zeile feld="Ausgeführt nach" wert={e.errichter.ausgefuehrtNach} />
          <Zeile feld="Zählpunktnummer" wert={e.projekt.zaehlpunktnummer} pflicht />
          <Zeile
            feld="Netzbetreiber"
            wert={e.projekt.netzbetreiber ?? e.netzvorgaben.netzbetreiber}
          />
          <Zeile feld="Inbetriebnahme" wert={tagAnzeige(e.projekt.inbetriebnahme) || null} />
        </Abschnitt>

        <Abschnitt nummer="2" titel="Art der PV-Anlage">
          <Zeile feld="Betriebsart" wert="Netzparallelbetrieb" />
          <Zeile
            feld="Art der Einspeisung"
            wert={
              e.projekt.einspeiseart === 'voll'
                ? 'Volleinspeisung'
                : e.projekt.einspeiseart === 'ueberschuss'
                  ? 'Überschusseinspeisung'
                  : null
            }
            pflicht
          />
        </Abschnitt>

        <Abschnitt nummer="3.1" titel="Solarmodule">
          {modul ? (
            <>
              <Zeile feld="Hersteller" wert={modul.hersteller} />
              <Zeile feld="Type" wert={modul.typ} />
              <Zeile feld="Nennleistung" wert={`${modul.nennleistung_wp} Wp`} />
              <Zeile feld="Leerlaufspannung U_oc" wert={`${modul.uoc_v} V`} />
              <Zeile feld="Betriebsstrom I_MPP" wert={modul.impp_a ? `${modul.impp_a} A` : null} />
              <Zeile feld="Anzahl Stränge" wert={String(offeneStrings.length)} />
              <Zeile
                feld="Anzahl Module je Strang"
                wert={
                  modulJeStrang.length === 0
                    ? null
                    : gleicheLaenge
                      ? String(modulJeStrang[0])
                      : modulJeStrang.join(', ')
                }
              />
              <Zeile
                feld={`Systemnennspannung (bei ${e.anlage.auslegung.kaltGradC} °C)`}
                wert={systemspannung ? `${systemspannung.toFixed(0)} V` : null}
              />
              <Zeile
                feld="Gesamtleistung Nennbedingungen"
                wert={`${e.leistungKwp.toFixed(2)} kWp`}
              />
            </>
          ) : (
            <p className="klein">Kein Modul gewählt.</p>
          )}
        </Abschnitt>

        <Abschnitt nummer="3.2" titel="Modulmontage">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={kopf}>Fläche</th>
                <th style={kopf}>Module</th>
                <th style={kopf}>Ausrichtung</th>
                <th style={kopf}>Neigung</th>
                <th style={kopf}>Art</th>
              </tr>
            </thead>
            <tbody>
              {e.flaechen.map((flaeche) => {
                const planung = e.planungen.get(flaeche.id);
                if (!planung || planung.zahlen.anzahl === 0) return null;
                return (
                  <tr key={flaeche.id}>
                    <td style={zelle}>{flaeche.name}</td>
                    <td style={zelle}>{planung.zahlen.anzahl}</td>
                    <td style={zelle}>
                      {planung.kennzahlen.azimutGrad.toFixed(0)}° (
                      {himmelsrichtung(planung.kennzahlen.azimutGrad)})
                    </td>
                    <td style={zelle}>{planung.kennzahlen.neigungGrad}°</td>
                    <td style={zelle}>
                      {flaeche.art === 'fassade'
                        ? 'Fassade'
                        : flaeche.aufstaenderung_grad
                          ? 'aufgeständert'
                          : 'dachparallel'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ fontSize: '0.72rem', color: 'var(--grau)', marginTop: '0.3rem' }}>
            Statische Vorbemessung für das Montagesystem: gesondert beizulegen. Das Programm rechnet
            keine Ballast- und Windlast.
          </p>
        </Abschnitt>

        <Abschnitt nummer="3.4" titel="Speicher">
          {speicher.length === 0 ? (
            <p className="klein">Kein Speicher vorgesehen.</p>
          ) : (
            speicher.map((pk) => {
              const s = stamm(pk);
              return (
                <div key={pk.id}>
                  <Zeile feld="Hersteller" wert={s?.hersteller} />
                  <Zeile feld="Type" wert={s?.typ} />
                  <Zeile
                    feld="Kapazität"
                    wert={s?.kenndaten.kapazitaetKwh ? `${s.kenndaten.kapazitaetKwh} kWh` : null}
                  />
                </div>
              );
            })
          )}
        </Abschnitt>

        <Abschnitt nummer="3.5" titel="Wechselrichter">
          {wechselrichter.length === 0 ? (
            <p className="klein">Kein Wechselrichter gewählt.</p>
          ) : (
            wechselrichter.map((pk) => {
              const w = stamm(pk);
              return (
                <div key={pk.id} style={{ marginBottom: '0.6rem' }}>
                  <Zeile feld="Hersteller" wert={w?.hersteller} />
                  <Zeile feld="Type" wert={w?.typ} />
                  <Zeile feld="Anzahl" wert={String(pk.anzahl)} />
                  <Zeile
                    feld="Max. Eingangsspannung DC"
                    wert={
                      w?.kenndaten.maxEingangsspannungV
                        ? `${w.kenndaten.maxEingangsspannungV} V`
                        : null
                    }
                  />
                  <Zeile
                    feld="MPP-Bereich ab"
                    wert={w?.kenndaten.mppUntergrenzeV ? `${w.kenndaten.mppUntergrenzeV} V` : null}
                  />
                  <Zeile
                    feld="AC-Nennleistung"
                    wert={w?.kenndaten.acLeistungKw ? `${w.kenndaten.acLeistungKw} kW` : null}
                  />
                  <Zeile
                    feld="Anzahl MPP-Tracker"
                    wert={w?.kenndaten.anzahlMppt ? String(w.kenndaten.anzahlMppt) : null}
                  />
                  <p style={{ fontSize: '0.72rem', color: 'var(--grau)' }}>
                    Konformitätsnachweis und Datenblatt sind beizulegen.
                  </p>
                </div>
              );
            })
          )}
        </Abschnitt>

        <Abschnitt nummer="3.7" titel="Überspannungsschutz">
          {ueberspannung.length === 0 ? (
            <p className="klein">Nicht vorgesehen.</p>
          ) : (
            ueberspannung.map((pk) => (
              <Zeile
                key={pk.id}
                feld={stamm(pk)?.typ ?? 'Überspannungsschutz'}
                wert={`${pk.anzahl} Stk`}
              />
            ))
          )}
        </Abschnitt>

        <Abschnitt nummer="3.8" titel="Netzeinspeisung">
          <Zeile
            feld="Anschluss"
            wert={e.projekt.phasen ? `${e.projekt.phasen}-phasig` : null}
            pflicht
          />
          <Zeile feld="Einspeisepunkt" wert={e.projekt.einspeisepunkt} pflicht />
          <Zeile feld="Art des Zählers" wert={e.projekt.zaehlerart} pflicht />
        </Abschnitt>

        {/* --- Verstringung, als Beilage zum Schaltplan ------------------ */}
        <Abschnitt nummer="3.9" titel="Verstringung">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th style={kopf}>Strang</th>
                <th style={kopf}>Module</th>
                <th style={kopf}>MPP-Tracker</th>
                <th style={kopf}>U_L bei {e.anlage.auslegung.kaltGradC} °C</th>
              </tr>
            </thead>
            <tbody>
              {offeneStrings.map((string) => (
                <tr key={string.id}>
                  <td style={zelle}>{string.name}</td>
                  <td style={zelle}>{string.module.length}</td>
                  <td style={zelle}>{string.mppt ?? '—'}</td>
                  <td style={zelle}>
                    {modul
                      ? `${stringSpannung(string.module.length, { uocV: Number(modul.uoc_v), tkUocProzentProK: Number(modul.tk_uoc_prozent_k) }, e.anlage.auslegung.kaltGradC, e.anlage.auslegung.referenzGradC).toFixed(0)} V`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Abschnitt>

        {/* --- Besichtigen und Erproben (Mathias, 11/2026) ----------------- */}
        <Abschnitt nummer="4" titel="Besichtigen und Erproben">
          <Pruefpunkte
            planung={planungspunkte({
              befund: e.anlage.befund,
              wechselrichter: !!e.anlage.wechselrichter,
              systemspannungBekannt: e.anlage.systemspannungBekannt,
              spannungsfaelle: [...e.anlage.spannungsfaelle.values()].flat(),
            })}
            punkte={e.pruefbefund?.messwerte.pruefpunkte ?? punkteAusVorlage(e.pruefvorlage)}
          />
        </Abschnitt>

        {/* --- Messwerte aus dem Pruefbefund ----------------------------- */}
        <Abschnitt nummer="5" titel="Messung">
          {e.pruefbefund?.messwerte.isolation ? (
            <>
              <Zeile
                feld="Prüfspannung"
                wert={
                  e.pruefbefund.messwerte.isolation.pruefspannungV
                    ? `${e.pruefbefund.messwerte.isolation.pruefspannungV} V`
                    : null
                }
              />
              <Zeile
                feld="Isolationswiderstand Plus/PE"
                wert={
                  e.pruefbefund.messwerte.isolation.plusPeMOhm
                    ? `${e.pruefbefund.messwerte.isolation.plusPeMOhm} MΩ`
                    : null
                }
              />
              <Zeile
                feld="Isolationswiderstand Minus/PE"
                wert={
                  e.pruefbefund.messwerte.isolation.minusPeMOhm
                    ? `${e.pruefbefund.messwerte.isolation.minusPeMOhm} MΩ`
                    : null
                }
              />
            </>
          ) : (
            <p className="klein">Noch keine Isolationsmessung eingetragen.</p>
          )}

          {(e.pruefbefund?.messwerte.strings ?? []).length > 0 && (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.8rem',
                marginTop: '0.5rem',
              }}
            >
              <thead>
                <tr>
                  <th style={kopf}>Strang</th>
                  <th style={kopf}>Leerlaufspannung</th>
                  <th style={kopf}>Kurzschlussstrom</th>
                </tr>
              </thead>
              <tbody>
                {(e.pruefbefund?.messwerte.strings ?? []).map((m) => (
                  <tr key={m.string}>
                    <td style={zelle}>{m.string}</td>
                    <td style={zelle}>{m.leerlaufspannungV ?? '—'} V</td>
                    <td style={zelle}>{m.kurzschlussstromA ?? '—'} A</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Abschnitt>

        {/* --- Ergebnis ---------------------------------------------------- */}
        <Abschnitt nummer="6" titel="Ergebnis">
          <Zeile
            feld="Art der Prüfung"
            wert={
              {
                erstpruefung: 'Erstprüfung',
                wiederkehrend: 'Wiederkehrende Prüfung',
                ausserordentlich: 'Außerordentliche Prüfung',
              }[e.pruefbefund?.messwerte.art ?? 'erstpruefung']
            }
          />
          <Zeile
            feld="Ergebnis"
            pflicht
            wert={
              e.pruefbefund?.messwerte.ergebnis
                ? {
                    in_ordnung: 'In Ordnung',
                    geringe_maengel: 'Geringe Mängel',
                    nicht_in_ordnung: 'Nicht in Ordnung',
                  }[e.pruefbefund.messwerte.ergebnis]
                : null
            }
          />
          {e.pruefbefund?.messwerte.ergebnis === 'geringe_maengel' && (
            <Zeile
              feld="Mängel beheben binnen"
              wert={
                e.pruefbefund.messwerte.maengelfristWochen != null
                  ? `${e.pruefbefund.messwerte.maengelfristWochen} Wochen`
                  : null
              }
              pflicht
            />
          )}
          <Zeile
            feld="Prüfmittel"
            wert={
              [
                e.pruefbefund?.messwerte.pruefmittel?.[0]?.hersteller,
                e.pruefbefund?.messwerte.pruefmittel?.[0]?.type,
                e.pruefbefund?.messwerte.pruefmittel?.[0]?.seriennummer
                  ? `Nr. ${e.pruefbefund.messwerte.pruefmittel[0].seriennummer}`
                  : undefined,
              ]
                .filter(Boolean)
                .join(' ') || null
            }
          />
          <Zeile feld="Bemerkungen" wert={e.pruefbefund?.bemerkungen ?? null} />
          <Zeile feld="Prüferin oder Prüfer" wert={e.pruefbefund?.pruefer ?? null} pflicht />
          <Zeile
            feld="Geprüft am"
            wert={
              e.pruefbefund?.geprueft_am
                ? tagAnzeige(e.pruefbefund.geprueft_am)
                : null
            }
            pflicht
          />
        </Abschnitt>

        {/* --- Errichter -------------------------------------------------- */}
        <Abschnitt nummer="7" titel="Errichter">
          <Zeile feld="Firma" wert={e.errichter.firma} pflicht />
          <Zeile
            feld="Anschrift"
            wert={
              [e.errichter.strasse, `${e.errichter.plz ?? ''} ${e.errichter.ort ?? ''}`.trim()]
                .filter(Boolean)
                .join(', ') || null
            }
          />
          <Zeile feld="Konzession" wert={e.errichter.konzession} pflicht />
          <p style={{ fontSize: '0.72rem', color: 'var(--grau)' }}>
            Das Prüfprotokoll darf nur ein konzessioniertes Unternehmen ausstellen, das damit die
            Haftung für die ordnungsgemäße Errichtung übernimmt. Die Konzession ist über
            firmen.wko.at nachprüfbar.
          </p>
        </Abschnitt>

        <footer style={{ marginTop: '2rem', fontSize: '0.72rem', color: 'var(--grau)' }}>
          <p>
            Aufbau nach dem Prüfprotokoll OVE/OENORM E-8101. Dieses Blatt füllt den Inhalt vor und
            ersetzt das amtliche Formular nicht.
          </p>
          <p>
            {e.netzvorgaben.hinweis ??
              'Die Fertigstellungsanzeige bringt das Elektrounternehmen selbst im Netzpartner-Portal ein.'}
          </p>
        </footer>
      </div>
    </div>
  );
}

const ERGEBNISTEXT: Record<string, string> = {
  io: 'i. O.',
  mangel: 'Mangel',
  entfaellt: 'entfällt',
  offen: 'offen',
};

/** Die Prüfpunkte als Tabelle — erst die gerechneten, dann die abgehakten. */
function Pruefpunkte({
  planung,
  punkte,
}: {
  planung: ReturnType<typeof planungspunkte>;
  punkte: ReturnType<typeof punkteAusVorlage>;
}): React.JSX.Element {
  const stand = pruefstand([...planung, ...punkte]);
  const zeilen: Array<{ abschnitt: string; text: string; ergebnis: string; bemerkung?: string | null }> = [
    ...planung.map((p) => ({ abschnitt: 'Aus der Planung', text: p.text, ergebnis: p.ergebnis, bemerkung: p.grund ?? null })),
    ...punkte.map((p) => ({ abschnitt: p.abschnitt, text: p.text, ergebnis: p.ergebnis ?? 'offen', bemerkung: p.bemerkung ?? null })),
  ];
  return (
    <>
      <p className="klein" style={{ margin: '0 0 0.4rem' }}>
        {stand.gesamt - stand.offen} von {stand.gesamt} Punkten geprüft
        {stand.mangel > 0 ? `, ${stand.mangel} mit Mangel` : ''}
        {stand.offen > 0 ? `, ${stand.offen} offen` : ''}.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
        <thead>
          <tr>
            <th>Prüfpunkt</th>
            <th style={{ width: '5rem' }}>Ergebnis</th>
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z, i) => (
            <Fragment key={i}>
              {(i === 0 || zeilen[i - 1]!.abschnitt !== z.abschnitt) && (
                <tr>
                  <td colSpan={2} style={{ fontWeight: 600, paddingTop: '0.5rem' }}>
                    {z.abschnitt}
                  </td>
                </tr>
              )}
              <tr>
                <td>
                  {z.text}
                  {z.bemerkung && <div style={{ color: 'var(--grau)' }}>{z.bemerkung}</div>}
                </td>
                <td
                  style={{
                    color: z.ergebnis === 'mangel' ? '#c05038' : z.ergebnis === 'offen' ? 'var(--grau)' : undefined,
                    fontWeight: z.ergebnis === 'mangel' ? 600 : undefined,
                  }}
                >
                  {ERGEBNISTEXT[z.ergebnis]}
                </td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Abschnitt({
  nummer,
  titel,
  children,
}: {
  nummer: string;
  titel: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section style={{ marginTop: '1.25rem' }}>
      <h2 style={{ borderBottom: '1px solid var(--linie)', paddingBottom: '0.4rem' }}>
        <span style={{ color: 'var(--grau)', marginRight: '0.6rem' }}>{nummer}</span>
        {titel}
      </h2>
      {children}
    </section>
  );
}

/** Eine Zeile des Datenblatts. Was fehlt, faellt auf statt leer zu bleiben. */
function Zeile({
  feld,
  wert,
  pflicht,
}: {
  feld: string;
  wert?: string | null;
  pflicht?: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.85rem', padding: '0.15rem 0' }}>
      <span style={{ width: '16rem', color: 'var(--grau)' }}>{feld}</span>
      <span style={{ color: wert ? undefined : '#c05038' }}>
        {wert ?? (pflicht ? 'fehlt — vor der Einreichung eintragen' : '—')}
      </span>
    </div>
  );
}

// Kopf und Zelle kommen aus dem CSS (th, td); hier bleibt nur die Ausrichtung.
const kopf: React.CSSProperties = {};

const zelle: React.CSSProperties = {};
